import { useState } from "react";
import { CloseGreyIcon, DeliveryBlackIcon, ShopLocalIcon, StoreBlackIcon } from "../../../../../assets/icons";
import PersonalPickUpTab from "./PersonalPickUpTab";
import DeliveryTab from "./DeliveryTab";
import { CreateOrderDeliveryDTO, CreateOrderOrderItemDTO, CreateOrderPickupDTO } from "../../../../../models/order/order.request";
import { useNotification } from "../../../../toster-notification/NotificationContext";
import { useAuth } from "../../../../../auth/AuthContext";
import { useParams } from "react-router-dom";
import Loader from "../../../../shared/Loader";
import { orderService } from "../../../../../services/orderService";
import { handleErrorNotification } from "../../../../../utils/errorNotificatioMessage";
import UnauthorizedUserModal from "../../../../shared/UnauthorizedUserModal";
import { useCart } from "../../../../../hooks/useCart";

type OnlineOrderTab = 'personal-pick-up' | 'delivery';

interface FinishOnlineOrderViewProps {
    onClose: () => void;
    selectedItems: CreateOrderOrderItemDTO[];
    totalPrice: number;
    clearCart?: () => void;
}

const FinishOnlineOrderView: React.FC<FinishOnlineOrderViewProps> = ({ onClose, selectedItems, totalPrice, clearCart }) => {

    const [selectedTab, setSelectedTab] = useState<OnlineOrderTab>('personal-pick-up');
    const [isForAnotherPerson, setIsForAnotherPerson] = useState<boolean>(false);
    const [pickUpAtError, setPickUpAtError] = useState<string>('');
    const [deliveryAddressError, setDeliveryAddressError] = useState<string>('');

    const [orderDeliveryData, setOrderDeliveryData] = useState<CreateOrderDeliveryDTO>({
        description: '',
        isForAnotherPerson: false,
        fullName: '',
        phoneNumber: '',
        email: '',
        street: '',
        streetNumber: '',
        apartment: '',
        userId: '',
        localId: '',
        items: []
    });

    const [orderPickupData, setOrderPickupData] = useState<CreateOrderPickupDTO>({
        description: '',
        isForAnotherPerson: false,
        fullName: '',
        phoneNumber: '',
        email: '',
        pickUpAt: '',
        userId: '',
        localId: '',
        items: []
    });

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();
    const { user } = useAuth();
    const localId = useParams().localId;


    const handleCreatePickUpOrder = async () => {

        setPickUpAtError('');

        if (!user || !localId) {
            notify('error', 'Korisnik nije prijavljen ili lokal nije pronađen.');
            return;
        }

        if (!orderPickupData.pickUpAt) {
            notify('error', 'Molimo unesite datum i vreme preuzimanja.');
            setPickUpAtError('Molimo unesite datum i vreme preuzimanja.');
            return;
        }

        setLoading(true);
        try {
            const pickUpUtc = new Date(orderPickupData.pickUpAt).toISOString();

            const response = await orderService.createPickupOrder(localId, { ...orderPickupData, pickUpAt: pickUpUtc, items: selectedItems, userId: user.id, localId, isForAnotherPerson: isForAnotherPerson });

            if (response.success && response.data) {
                notify('success', 'Uspešno ste kreirali porudžbinu. Broj porudžbine: ' + response.data);
                clearCart?.();
                onClose();
            }
        } catch (error) {
            notify('error', "Došlo je do greške prilikom kreiranja porudžbine. Pokušajte ponovo.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDeliveryOrder = async () => {

        setDeliveryAddressError('');

        if (!user || !localId) {
            notify('error', 'Korisnik nije prijavljen ili lokal nije pronađen.');
            return;
        }

        if (!orderDeliveryData.street || !orderDeliveryData.streetNumber) {
            notify('error', 'Molimo unesite adresu dostave.');
            setDeliveryAddressError('Molimo unesite adresu dostave.');
            return;
        }

        setLoading(true);
        try {
            const response = await orderService.createDeliveryOrder(localId, { ...orderDeliveryData, items: selectedItems, userId: user.id, localId, isForAnotherPerson: isForAnotherPerson });

            if (response.success && response.data) {
                notify('success', 'Uspešno ste kreirali porudžbinu. Broj porudžbine: ' + response.data);
                clearCart?.();
                onClose();
            } else {
                notify('error', "Došlo je do greške prilikom kreiranja porudžbine. Pokušajte ponovo.");
            }
        } catch (error) {
            notify('error', "Došlo je do greške prilikom kreiranja porudžbine. Pokušajte ponovo.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    if (!user || !user.id) {
        return <UnauthorizedUserModal
            onClose={onClose}
        />;
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section finish-online-order">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Online Poručivanje</div>
                                <div className="description-text">
                                    Izaberite način preuzimanja vaše porudžbine<br />
                                </div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="modal-form-block w-form">
                            <div className={`small-box-div pointer ${isForAnotherPerson ? 'green-bg' : 'disable'}`} onClick={() => setIsForAnotherPerson(!isForAnotherPerson)}>
                                <div>Porudžbina za drugu osobu</div>
                            </div>
                            <form className="modal-form">

                                {isForAnotherPerson &&
                                    <>
                                        <div className="modal-form-div-group">
                                            <div className="modal-group-header">Vaši podaci</div>
                                            <div className="modal-form-div">
                                                <label className="modal-form-label">Ime i prezime *</label>
                                                <input className="form-input grey w-input" placeholder="Ime i prezime" type="text"
                                                    onChange={(e) => {
                                                        setOrderDeliveryData({ ...orderDeliveryData, fullName: e.target.value });
                                                        setOrderPickupData({ ...orderPickupData, fullName: e.target.value });
                                                    }}
                                                />
                                            </div>
                                            <div className="div-grid-2">
                                                <div className="modal-form-div">
                                                    <label className="modal-form-label">Broj telefona *</label>
                                                    <input className="form-input grey w-input" placeholder="Broj telefona" type="number"
                                                        onChange={(e) => {
                                                            setOrderDeliveryData({ ...orderDeliveryData, phoneNumber: e.target.value });
                                                            setOrderPickupData({ ...orderPickupData, phoneNumber: e.target.value });
                                                        }}
                                                    />
                                                </div>
                                                <div className="modal-form-div">
                                                    <label className="modal-form-label">Email *</label>
                                                    <input className="form-input grey w-input" placeholder="Email" type="email"
                                                        onChange={(e) => {
                                                            setOrderDeliveryData({ ...orderDeliveryData, email: e.target.value });
                                                            setOrderPickupData({ ...orderPickupData, email: e.target.value });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-hr"></div>
                                    </>
                                }

                                <div className="tab-navigation-list-div">
                                    <div className={`tab-navigation-div ${selectedTab === 'personal-pick-up' ? 'selected' : ''}`} onClick={() => setSelectedTab('personal-pick-up')} >
                                        <img src={StoreBlackIcon} loading="lazy" alt="" className="icon-size-16" />
                                        <div>Lično preuzimanje</div>
                                    </div>
                                    <div className={`tab-navigation-div ${selectedTab === 'delivery' ? 'selected' : ''}`} onClick={() => setSelectedTab('delivery')}>
                                        <img src={DeliveryBlackIcon} loading="lazy" alt="" className="icon-size-16" />
                                        <div>Dostava</div>
                                    </div>
                                </div>

                                {selectedTab === 'personal-pick-up' &&
                                    <PersonalPickUpTab
                                        orderPickupData={orderPickupData}
                                        onChange={setOrderPickupData}
                                        pickUpAtError={pickUpAtError}
                                        totalAmount={totalPrice}
                                    />
                                }

                                {selectedTab === "delivery" &&
                                    <DeliveryTab
                                        orderDeliveryData={orderDeliveryData}
                                        onChange={setOrderDeliveryData}
                                        deliveryAddressError={deliveryAddressError}
                                        totalAmount={totalPrice}
                                    />
                                }

                            </form>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium">
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium client-gold" onClick={() => selectedTab === 'personal-pick-up' ? handleCreatePickUpOrder() : handleCreateDeliveryOrder()}>
                                <div>Potvrdi Porudžbinu</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    );
};

export default FinishOnlineOrderView;